import { CallIntelTypes } from '@frontend/api-call-intel';

export type ExtendedCallIntelSubViewType = CallIntelTypes.CallIntelSubViewType | 'follow-up';

export type ExtendedCallIntelSubView =
  | {
      type: 'sentiment';
      id?: CallIntelTypes.SentimentEnum;
    }
  | {
      type: 'category';
      id?: CallIntelTypes.CategoryEnum;
    }
  | {
      type: 'appointment-type';
      id?: CallIntelTypes.AppointmentTypeEnum;
    }
  | {
      type: 'service-quality';
      id?: CallIntelTypes.ServiceQualityFlagEnum;
    }
  | {
      type: 'follow-up';
      id?: CallIntelTypes.TaskType | 'all';
    };

export type SubViewTypeConfig = {
  getRoute: (id: string) => string;
  includeTypeInSearch: boolean;
};

export type NavigationConfig = Record<ExtendedCallIntelSubViewType, SubViewTypeConfig>;

// ==================== Service Issues ====================

/**
 * Service quality flags with dedicated routes
 * Based on: libs/apis/api-call-intel/src/constant.ts:AllowedServiceIssuesRouteLabelMap
 */
const SERVICE_ISSUE_MAPPING: Partial<Record<CallIntelTypes.ServiceQualityFlagEnum, string>> = {
  [CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: 'treatment-concerns',
  [CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: 'unresolved-contact-attempts',
  [CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: 'manager-escalations',
};

const serviceIssueConverters = {
  toSlug: (enumValue: CallIntelTypes.ServiceQualityFlagEnum): string => {
    return SERVICE_ISSUE_MAPPING[enumValue] ?? '';
  },
};

// ==================== Follow-Ups ====================

/**
 * Task type to slug converter
 */
const TASK_TYPE_PREFIX = 'TYPE_';

const taskTypeConverters = {
  toSlug: (taskType: string): string => {
    if (taskType === 'all') return 'all';
    return taskType.replace(TASK_TYPE_PREFIX, '').toLowerCase();
  },
};

// ==================== Navigation Config ====================

export const CALL_INTEL_NAVIGATION_CONFIG: NavigationConfig = {
  sentiment: {
    getRoute: (id: string) => `/analytics/call-intelligence/${id.toLowerCase()}`,
    includeTypeInSearch: true,
  },
  category: {
    getRoute: (id: string) => `/analytics/call-intelligence/${id.toLowerCase()}`,
    includeTypeInSearch: true,
  },
  'appointment-type': {
    getRoute: (id: string) => `/analytics/call-intelligence/${id.toLowerCase()}`,
    includeTypeInSearch: true,
  },
  'service-quality': {
    getRoute: (id: string) =>
      `/analytics/call-intelligence/service-issue/${serviceIssueConverters.toSlug(
        id as CallIntelTypes.ServiceQualityFlagEnum
      )}`,
    includeTypeInSearch: false,
  },
  'follow-up': {
    getRoute: (id: string) => `/analytics/call-intelligence/follow-ups/${taskTypeConverters.toSlug(id)}`,
    includeTypeInSearch: false,
  },
};

// ==================== Helper Functions ====================

/**
 * Get the route for a given subView
 */
export const getSubViewRoute = (subView: ExtendedCallIntelSubView): string | null => {
  if (!subView.type || !subView.id) return null;

  const config = CALL_INTEL_NAVIGATION_CONFIG[subView.type];
  if (!config) return null;

  return config.getRoute(subView.id);
};

/**
 * Get search params for a given subView
 */
export const getSubViewSearchParams = (subView: ExtendedCallIntelSubView): Record<string, string> => {
  if (!subView.type || !subView.id) return {};

  const config = CALL_INTEL_NAVIGATION_CONFIG[subView.type];
  if (!config) return {};

  return config.includeTypeInSearch ? { type: subView.type } : {};
};
